import {expectEither, HttpClient, parseResponseAs, useHttpClient} from '@raison/network-facade';
import {Decoder} from '@raison/prelude';
import * as Sentry from '@sentry/react';
import {infiniteQueryOptions} from '@tanstack/react-query';
import {
    PaginatedTransactionsSchema,
    PaginatedTransactionsWeakSchema,
    TransactionTypeName,
} from '../model/TransactionSchema';
import {TransactionsHistory} from '../model/TransactionsHistory';

interface Params extends TransactionsHistory.Filters {
    limit?: number;
}

interface PagingParams extends Params {
    last: number;
}

export function useTransactionsHistoryOptions({limit = 10, ...filters}: Params = {}) {
    const http = useHttpClient();

    const options = infiniteQueryOptions({
        queryKey: ['/v1/search/orders', limit, filters] as const,
        async queryFn({client, pageParam, queryKey}) {
            const [, limit, filters] = queryKey;
            const response = await fetchTransactionsHistory(http, {
                ...filters,
                limit,
                last: pageParam,
            });
            if (response.status === 'Abort') {
                await client.cancelQueries({queryKey});
                throw response.reason;
            }
            return response.json;
        },
        initialPageParam: 0,
        getNextPageParam: lastPage => lastPage.last,
        select: data => {
            const orders = data.pages.flatMap(page => page.orders);
            const total = data.pages[0].total;
            return {orders, total};
        },
    });

    return {
        queryKey: options.queryKey,
        queryFn: options.queryFn!,
        initialPageParam: options.initialPageParam,
        getNextPageParam: options.getNextPageParam,
        select: options.select,
    };
}

async function fetchTransactionsHistory(http: HttpClient, {limit = 10, ...filters}: PagingParams) {
    const params = TransactionsHistory.filtersToUrlParams({...filters, limit});
    return await http(`/v1/search/orders?${params}`).then(
        ...expectEither({
            200: {
                parse: parseTransactionHistory,
            },
        })
    );
}

async function parseTransactionHistory(response: HttpClient.SyncOutput) {
    const weak = await parseResponseAs(PaginatedTransactionsWeakSchema, response);
    const unknownTypeErrors = new Set<string>();
    const filtered = Decoder.run(PaginatedTransactionsSchema, {
        ...weak,
        orders: weak.orders.filter(t => {
            const isKnownType = Decoder.check(TransactionTypeName)(t.type);
            if (!isKnownType) {
                unknownTypeErrors.add(`- Type "${t.type}" in order #${t.id}`);
            }
            return isKnownType;
        }),
    }).orThrow();

    if (unknownTypeErrors.size > 0) {
        reportMessage('Unknown order types:\n' + Array.from(unknownTypeErrors).join('\n'));
    }

    return filtered;
}

let reportMessage = console.log;
if (import.meta.env.PROD) {
    reportMessage = Sentry.captureMessage;
}
